/**
 * Single source of truth for all brand colors.
 * When re-skinning the project, change values ONLY in this file.
 * All other files (tailwind.config.ts, themeColors.ts, pdfTokens.ts, chart components) import from here.
 */
export const colors = {
  // Deutsche Bank demo palette
  peach: {
    DEFAULT: '#F5F7FB',
    1: '#E1E7F2',
    2: '#C4CFDF',
    3: '#9EADC2',
  },
  pif: {
    green: '#0018A8',
    greenLight: '#173DCC',
    gold: '#6C7A92',
  },

  // Grey scale — each shade has light-mode and dark-mode value
  grey: {
    black:       { light: '#111827', dark: '#FFFFFF' },
    main:        { light: '#263244', dark: '#E7ECF5' },
    hover:       { light: '#64748B', dark: '#B8C2D4' },
    pseudohover: { light: '#475569', dark: '#F5F7FB' },
    medium:      { light: '#A8B4C7', dark: '#64748B' },
    plain:       { light: '#E1E7F2', dark: '#263244' },
    light:       { light: '#F5F7FB', dark: '#0B1020' },
    sky:         { light: '#FBFCFF', dark: '#070B16' },
    // Fallback color for sheets without an assigned color
    sheetFallback: '#AABCC5',
  },

  white: { light: '#FFFFFF', dark: '#0B1020' },
  black: { light: '#0B1020', dark: '#F5F7FB' },

  // Semantic status colors
  negative: {
    primary:  { light: '#CA325F', dark: '#D94773' },
    hover:    { light: '#D94773', dark: '#CA325F' },
    disable:  '#D8A3BB',
    subtitle: '#E2BDCE',
  },
  positive: {
    primary:  { light: '#0018A8', dark: '#5B7CFF' },
    hover:    { light: '#173DCC', dark: '#335CF0' },
    disable:  '#A8B4E8',
    subtitle: '#C7D2FE',
  },

  brand: {
    main:  '#0018A8',
    gold:  '#6C7A92',
    cream: '#F5F7FB',
    dark:  '#0B1020',
  },

  // Chart series palettes (recharts + PDF)
  chartSeries: {
    light: {
      primary:   '#0018A8',
      secondary: '#6C7A92',
      success:   '#167A5B',
      warning:   '#D98B2B',
      danger:    '#CA325F',
      neutral:   '#74716E',
      accent_1:  '#173DCC',
      accent_2:  '#D94773',
      accent_3:  '#5A5855',
    },
    dark: {
      primary:   '#5B7CFF',
      secondary: '#A8B4C7',
      success:   '#4AB58F',
      warning:   '#E6A64A',
      danger:    '#D94773',
      neutral:   '#CAC1AF',
      accent_1:  '#0018A8',
      accent_2:  '#CA325F',
      accent_3:  '#74716E',
    },
  },

  // Avatar / account accent colors (unique vivid palette, not part of main brand)
  avatar: ['#FEAD10', '#3362BD', '#43802A', '#FF67ED', '#00EFFE'],

  // Mermaid diagram theme variables
  mermaid: {
    light: {
      background:         '#ffffff',
      mainBkg:            '#ffffff',
      primaryColor:       '#dbeafe',
      primaryTextColor:   '#1f2937',
      primaryBorderColor: '#bfdbfe',
      secondaryColor:     '#f1f5f9',
      secondaryTextColor: '#1f2937',
      tertiaryColor:      '#e2e8f0',
      lineColor:          '#94a3b8',
      noteBkgColor:       '#f8fafc',
      noteTextColor:      '#1f2937',
      actorTextColor:     '#1f2937',
      clusterBkg:         '#f8fafc',
      clusterBorder:      '#cbd5e1',
      edgeLabelBackground:'#e2e8f0',
    },
    dark: {
      background:           '#0e1213',
      mainBkg:              '#0e1213',
      primaryColor:         '#262d30',
      primaryTextColor:     '#f2f6f7',
      primaryBorderColor:   '#4d5a62',
      secondaryColor:       '#1a1f20',
      secondaryTextColor:   '#f2f6f7',
      tertiaryColor:        '#111516',
      lineColor:            '#4d5a62',
      noteBkgColor:         '#111516',
      noteTextColor:        '#f2f6f7',
      actorTextColor:       '#f2f6f7',
      clusterBkg:           '#111516',
      clusterBorder:        '#4d5a62',
      edgeLabelBackground:  '#1a1f20',
      pieSectionTextColor:  '#f2f6f7',
    },
  },

  // Named shadow tokens (used in tailwind.config.ts boxShadow extension)
  shadows: {
    card:     '0 8px 16px 0 rgba(17, 21, 22, 0.16)',
    subtle:   '0 2px 12px rgba(0, 0, 0, 0.04)',
    compose:  '0 4px 24px rgba(0, 0, 0, 0.10)',
    workflow: '0 4px 20px rgba(0, 24, 168, 0.10)',
  },
} as const;
